/* GrowthOS — Bucket List (route /settings/bucketList, also rendered in-shell as
   the settings "Bucket List" nav item via the `embedded` prop).
   Reskinned from the legacy AG-grid + Bootstrap page to a themed table on the
   shared `.gos-tm` scope (same shell as Team / Bucket Management) — no new CSS.
   Lists ALL buckets (getBuckets = Bucket/GetAllBuckets, the legacy scope);
   create/edit reuse the already-GrowthOS `AddBucketForm`; delete is the real
   `deleteBuckets` (Bucket/Delete) behind a themed confirm modal (`.gos-import-*`).
   Owner-name resolution + access-level rules live in AddBucketForm. */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiServices from '../../services/service';
import { getUserList } from '../../redux/selector';
import { Ic } from '../../shared/component/GosIcon';
import { DateFormatter } from '../../shared/controls/formatters';
import AddBucketForm from '../bucketManagement/AddBucketForm';

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const colorFor = (s) => AVA_COLORS[hashIdx(String(s || ''), AVA_COLORS.length)];

const isPrivate = (b) => String(b?.bucketVisibility || '').toLowerCase() === 'private';
const accessOf = (b) => b?.accessLevel || b?.access || '';

const Skel = ({ w, h = 10, r = 5, style }) => (
  <span className="gos-skel-an" style={{ display: 'block', width: w, height: h, borderRadius: r, ...style }} />
);

const TableSkeleton = ({ rows = 6 }) => (
  <div className="gos-tm-tblscroll">
    <table className="gos-tm-tbl">
      <tbody>
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            <td>
              <div className="gos-tm-row" style={{ gap: 10 }}>
                <span className="gos-skel-an" style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0 }} />
                <Skel w={160} h={10} />
              </div>
            </td>
            <td><Skel w="50%" h={10} /></td>
            <td><Skel w="55%" h={10} /></td>
            <td><Skel w="45%" h={10} /></td>
            <td><Skel w={60} h={10} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function ConfirmDelete({ bucket, deleting, onCancel, onConfirm }) {
  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="gos-import-modal" role="dialog" aria-modal="true" style={{ maxWidth: 440 }}>
        <div className="gos-import-head">
          <div className="gos-import-head-l">
            <span className="gos-import-step-ic" style={{ background: 'var(--gos-red-bg)', color: 'var(--gos-red-fg)' }}>
              <Ic name="trash" size={16} />
            </span>
            <div>
              <div className="gos-import-title">Delete bucket</div>
              <div className="gos-import-sub">This action cannot be undone.</div>
            </div>
          </div>
          <button type="button" className="gos-import-close" onClick={onCancel} aria-label="Close">
            <Ic name="x" size={18} />
          </button>
        </div>
        <div className="gos-import-body">
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--gos-text-2)', lineHeight: 1.5 }}>
            Are you sure you want to delete <strong style={{ color: 'var(--gos-text)' }}>{bucket?.name || 'this bucket'}</strong>?
          </p>
        </div>
        <div className="gos-import-foot">
          <button type="button" className="gos-btn gos-btn-ghost" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button type="button" className="gos-btn gos-btn-danger" onClick={onConfirm} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete Bucket'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BucketList({ embedded = false }) {
  const usersList = useSelector(getUserList);
  const Wrap = ({ children }) => (embedded ? <>{children}</> : <div className="inner-page">{children}</div>);

  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadBuckets = () => {
    setLoading(true);
    apiServices
      .getBuckets()
      .then((res) => {
        const raw = res.data;
        const list = Array.isArray(raw) ? raw : raw?.bucketList || raw?.buckets || raw?.data || [];
        setBuckets(list);
      })
      .catch((e) => console.error('getBuckets', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadBuckets(); }, []);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return buckets;
    return buckets.filter((b) =>
      `${b.name || ''} ${accessOf(b)} ${b.bucketVisibility || ''}`.toLowerCase().includes(s)
    );
  }, [buckets, q]);

  const openCreate = () => { setEditData(null); setFormOpen(true); };
  const openEdit = (bucket) => { setEditData(bucket); setFormOpen(true); };

  const confirmDelete = () => {
    if (!toDelete) return;
    setDeleting(true);
    apiServices
      .deleteBuckets({ id: toDelete.id })
      .then(() => {
        toast.success('Bucket deleted successfully!', { position: 'top-right' });
        setToDelete(null);
        loadBuckets();
      })
      .catch((error) =>
        toast.error(error?.response?.data?.message || error.message || 'Failed to delete bucket', { position: 'top-right' })
      )
      .finally(() => setDeleting(false));
  };

  return (
    <Wrap>
      <div className="gos-tm">
        <div className="gos-tm-head">
          <div>
            <div className="gos-tm-title">Bucket List</div>
            <div className="gos-tm-sub">Every bucket in the workspace — search, edit, or remove.</div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={openCreate}>
            <Ic name="plus" size={16} />New Bucket
          </button>
        </div>

        <div className="gos-tm-toolbar">
          <div className="gos-tm-search">
            <Ic name="search" size={16} />
            <input placeholder="Search in buckets…" value={q} onChange={(e) => setQ(e.target.value)} />
            {q ? <button type="button" className="gos-tm-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
          </div>
          <span className="gos-tm-count"><strong>{rows.length}</strong> buckets</span>
        </div>

        <div className="gos-tm-cardbox" style={{ padding: 0, overflow: 'visible' }}>
          {loading ? (
            <TableSkeleton />
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl">
                  <thead>
                    <tr>
                      <th>Bucket Name</th>
                      <th>Access Level</th>
                      <th>Visibility</th>
                      <th>Expiry Date</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((b) => {
                      const tint = colorFor(b.id);
                      const priv = isPrivate(b);
                      return (
                        <tr key={b.id}>
                          <td>
                            <span className="gos-tm-row" style={{ gap: 10 }}>
                              <span className="gos-tm-card-badge" style={{ background: `${tint}1f`, color: tint, width: 32, height: 32 }}>
                                <Ic name="inbox" size={16} />
                              </span>
                              <span className="gos-tm-cellname">{b.name || '—'}</span>
                            </span>
                          </td>
                          <td>{accessOf(b) ? <span className="gos-tm-type">{accessOf(b)}</span> : <span className="gos-tm-muted">—</span>}</td>
                          <td><span className={'gos-tm-role-tag ' + (priv ? 'member' : 'captain')}>{priv ? 'Private' : 'Public'}</span></td>
                          <td><span className="gos-tm-cell gos-tm-muted">{DateFormatter(b.expiryDateTime) || '—'}</span></td>
                          <td>
                            <div className="gos-tm-row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                              <button type="button" className="gos-tm-icon-btn" title="Edit bucket" onClick={() => openEdit(b)}>
                                <Ic name="edit" size={15} />
                              </button>
                              <button
                                type="button"
                                className="gos-tm-icon-btn"
                                title="Delete bucket"
                                style={{ color: 'var(--gos-red-fg)' }}
                                onClick={() => setToDelete(b)}
                              >
                                <Ic name="trash" size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 ? (
                <div className="gos-tm-empty">{buckets.length ? 'No buckets match your search.' : 'No buckets yet — click New Bucket.'}</div>
              ) : null}
            </>
          )}
        </div>
      </div>

      <AddBucketForm
        open={formOpen}
        editData={editData}
        usersList={usersList}
        onClose={() => { setFormOpen(false); setEditData(null); }}
        onSave={() => { setFormOpen(false); setEditData(null); loadBuckets(); }}
      />

      {toDelete && (
        <ConfirmDelete
          bucket={toDelete}
          deleting={deleting}
          onCancel={() => !deleting && setToDelete(null)}
          onConfirm={confirmDelete}
        />
      )}
    </Wrap>
  );
}

export default BucketList;
